import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  FULLSTORY_EVENTS,
  PlatformInfo,
  detectArchitecture,
  detectPlatform,
  downloadDesktopApp,
  getAllPlatforms,
  getPlatformConfig,
} from '@fly/shared';

import { useFullStory } from './useFullStory';

export type DownloadSource = 'download' | 'home';

export interface UseDownloadReturn {
  detectedConfig: PlatformInfo | null;
  alternativePlatforms: PlatformInfo[];
  handleDownload: (config: PlatformInfo) => void;
}

const EVENTS_BY_SOURCE = {
  download: FULLSTORY_EVENTS.DOWNLOAD,
  home: FULLSTORY_EVENTS.HOME,
} as const;

export const useDownload = (
  source: DownloadSource = 'download'
): UseDownloadReturn => {
  const { trackEvent } = useFullStory();
  const hasTrackedView = useRef(false);
  const events = EVENTS_BY_SOURCE[source];

  const detectedConfig = useMemo(() => {
    return getPlatformConfig(detectPlatform(), detectArchitecture());
  }, []);

  const alternativePlatforms = useMemo(() => {
    const all = getAllPlatforms();
    return detectedConfig
      ? all.filter((p) => p.key !== detectedConfig.key)
      : all;
  }, [detectedConfig]);

  // Track page view
  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;

    trackEvent(events.PAGE_VIEWED, {
      detected_platform: detectedConfig?.key,
    });
  }, [trackEvent, detectedConfig, events]);

  const handleDownload = useCallback(
    (config: PlatformInfo) => {
      trackEvent(events.STARTED, {
        platform: config.key,
        is_detected: config.key === detectedConfig?.key,
      });

      downloadDesktopApp(config);
    },
    [trackEvent, detectedConfig?.key, events]
  );

  return {
    detectedConfig,
    alternativePlatforms,
    handleDownload,
  };
};
